import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Footer from '../components/Footer';

/**
 * La teoria, servida des d'un fitxer Markdown.
 *
 * Fins al 06/08/2026 el text vivia repartit en **179 claus de traducció** dins
 * de `locales/*.json`, amb un `theory/ACHbasis.md` al repositori que no llegia
 * ningú. Dues conseqüències: el fitxer que semblava el material no era el que
 * veien els alumnes, i **qui ensenya no podia editar el text** sense obrir un
 * JSON i anar clau per clau.
 *
 * Ara la font és `public/theory/ACH-<idioma>.md`. S'edita com un document, els
 * capítols de l'índex surten dels seus encapçalaments `##`, i canviar el text
 * no demana tocar cap component.
 *
 * Els blocs que han de connectar amb l'eina —l'escala de valoració, de moment—
 * s'insereixen amb una marca dins del text: `<!--escala-->`.
 */

const VALORACIONS = ['CC', 'C', 'N', 'I', 'II'];
const NOM_VALORACIO = {
  CC: 'matrix.veryConsistent',
  C: 'matrix.consistent',
  N: 'matrix.neutral',
  I: 'matrix.inconsistent',
  II: 'matrix.veryInconsistent',
};

const MARCA_ESCALA = '<!--escala-->';

/** «Pas 4: la matriu» → «pas-4-la-matriu», per poder-hi enllaçar. */
function llenega(text) {
  return String(text)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

function textDe(fills) {
  if (typeof fills === 'string') return fills;
  if (Array.isArray(fills)) return fills.map(textDe).join('');
  if (fills && fills.props) return textDe(fills.props.children);
  return '';
}

/** L'escala, amb els mateixos colors que la matriu de l'examen. */
function EscalaValoracio() {
  const { t } = useTranslation();
  return (
    <div className="teoria-escala">
      {VALORACIONS.map((valor) => (
        <div key={valor} className="teoria-escala-item">
          <span className={`rating-option is-selected rating-${valor}`}>{valor}</span>
          <span>{t(NOM_VALORACIO[valor])}</span>
        </div>
      ))}
    </div>
  );
}

function Theory() {
  const { t, i18n } = useTranslation();
  const [text, setText] = useState('');
  const [capitolActiu, setCapitolActiu] = useState('');
  const [error, setError] = useState(false);

  // El català és la versió de referència; l'anglès, si encara no existeix, hi
  // cau. És la mateixa regla que la resta del contingut (vegeu §Idiomes).
  useEffect(() => {
    let cancellat = false;
    const idioma = (i18n.language || '').startsWith('en') ? 'en' : 'ca';

    const carrega = async () => {
      for (const codi of [idioma, 'ca']) {
        try {
          const resposta = await fetch(`${process.env.PUBLIC_URL || ''}/theory/ACH-${codi}.md`);
          if (!resposta.ok) continue;
          const cos = await resposta.text();
          // Si el fitxer no hi és, nginx retorna l'index.html de l'aplicació
          // (`try_files ... /index.html`) amb un 200: sense aquesta comprovació
          // ensenyaríem el codi HTML com si fos el text de la teoria.
          if (cos.trimStart().startsWith('<')) continue;
          if (!cancellat) {
            setText(cos);
            setError(false);
          }
          return;
        } catch (e) {
          // provem l'idioma següent
        }
      }
      if (!cancellat) setError(true);
    };

    carrega();
    return () => { cancellat = true; };
  }, [i18n.language]);

  // Els capítols de l'índex són els encapçalaments de segon nivell del text.
  const capitols = useMemo(() => (
    text
      .split('\n')
      .filter((linia) => linia.startsWith('## '))
      .map((linia) => {
        const titol = linia.replace(/^##\s+/, '').trim();
        return { id: llenega(titol), titol };
      })
  ), [text]);

  useEffect(() => {
    if (!capitols.length) return undefined;
    const alDesplacar = () => {
      const actual = capitols.find(({ id }) => {
        const node = document.getElementById(id);
        if (!node) return false;
        const caixa = node.getBoundingClientRect();
        return caixa.top >= 0 && caixa.top < window.innerHeight / 2;
      });
      if (actual) setCapitolActiu(actual.id);
    };
    window.addEventListener('scroll', alDesplacar);
    alDesplacar();
    return () => window.removeEventListener('scroll', alDesplacar);
  }, [capitols]);

  const veAlCapitol = (id) => {
    setCapitolActiu(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const components = useMemo(() => ({
    h2: ({ node, children, ...props }) => (
      <h2 id={llenega(textDe(children))} {...props}>{children}</h2>
    ),
    h3: ({ node, children, ...props }) => (
      <h3 id={llenega(textDe(children))} {...props}>{children}</h3>
    ),
    // Les taules d'exemple es dibuixen com la matriu de l'examen, i les
    // cel·les que porten un valor de l'escala prenen el seu color: qui llegeix
    // l'exemple està mirant la mateixa graella que després haurà d'omplir.
    table: ({ node, children, ...props }) => (
      <div className="teoria-taula">
        <table {...props}>{children}</table>
      </div>
    ),
    td: ({ node, children, ...props }) => {
      const valor = textDe(children).trim();
      const esValoracio = VALORACIONS.includes(valor);
      return (
        <td className={esValoracio ? `teoria-cel-valor rating-${valor}` : undefined} {...props}>
          {children}
        </td>
      );
    },
  }), []);

  // El text es parteix per les marques dels blocs interactius.
  const trossos = useMemo(() => text.split(MARCA_ESCALA), [text]);

  return (
    <div className="theory-container">
      <div className="theory-header">
        <h1>{t('theory.achTitle')}</h1>
        <p className="theory-subtitle">{t('theory.subtitle')}</p>
      </div>

      <div className="theory-layout">
        <nav className="theory-nav">
          <div className="nav-sticky">
            <h3>{t('theory.tableOfContents')}</h3>
            <ul className="theory-toc">
              {capitols.map(({ id, titol }) => (
                <li key={id}>
                  <button
                    className={capitolActiu === id ? 'active' : ''}
                    onClick={() => veAlCapitol(id)}
                  >
                    {titol}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <div className="theory-content theory-md">
          {error && <p className="text-muted">{t('theory.loadError')}</p>}
          {trossos.map((tros, i) => (
            <React.Fragment key={i}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{tros}</ReactMarkdown>
              {i < trossos.length - 1 && <EscalaValoracio />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Theory;
